import * as React from "react";
import { useAdminFree30, IRankedCoach } from "./use-gym-wide";
import {
  gymDisplayName,
  monthDisplayName,
  formatRate,
  formatNumber,
  getShowRate,
  getConversionRate,
  getFreeSessionsBooked,
  getFreeSessionsAttended,
  getFreeSessionsConverted
} from "./use-coach-performance";
import { ICoach } from "./coach-performance.types";

import styles from "./gym-wide.module.css";

/* ---------------------------- helpers ---------------------------------- */

interface IKpiRanking {
  key: string;
  title: string;
  pickRate: (c: ICoach) => number | null;
  pickDetail: (c: ICoach) => string;
  /**
   * Sample-size floor so 1/1 = 100% coaches don't dominate the leaderboard.
   */
  minSampleField: (c: ICoach) => number;
}

const KPI_RANKINGS: IKpiRanking[] = [
  {
    key: "conversion",
    title: "Conversion Rate",
    pickRate: c => getConversionRate(c),
    pickDetail: c =>
      `${getFreeSessionsConverted(c)}/${getFreeSessionsAttended(c)}`,
    minSampleField: c => getFreeSessionsAttended(c)
  },
  {
    key: "show",
    title: "Show Rate",
    pickRate: c => getShowRate(c),
    pickDetail: c =>
      `${getFreeSessionsAttended(c)}/${getFreeSessionsBooked(c)}`,
    minSampleField: c => getFreeSessionsBooked(c)
  }
];

interface IKpiRankEntry {
  name: string;
  gym: string;
  rate: number | null;
  detail: string;
}

function pickTopN(
  coaches: IRankedCoach[],
  ranking: IKpiRanking,
  n: number
): IKpiRankEntry[] {
  return coaches
    .filter(rc => ranking.minSampleField(rc.coach) >= 3)
    .filter(rc => ranking.pickRate(rc.coach) !== null)
    .sort((a, b) => {
      const ra = ranking.pickRate(a.coach) ?? 0;
      const rb = ranking.pickRate(b.coach) ?? 0;
      return rb - ra;
    })
    .slice(0, n)
    .map(rc => ({
      name: rc.coach.name,
      gym: gymDisplayName(rc.subdomain, rc.gymName),
      rate: ranking.pickRate(rc.coach),
      detail: ranking.pickDetail(rc.coach)
    }));
}

/* ------------------------ ranking table cells -------------------------- */

interface IRankingRowProps {
  rank: number | null;
  rc: IRankedCoach;
  highlightCoachId?: number | null;
}

/**
 * Compute the five compliance percentages from coach.free30_process_metrics.
 * Returns "—" for any column the API can't express as a per-lead rate.
 * "Call" stays "—" because the API exposes a total (`pre_session_calls_placed_count`)
 * across all leads, not a per-lead boolean — same caveat as Row 3 on the
 * per-coach view. Replace with a proper per-lead rate when the API ships one.
 */
function complianceCells(coach: ICoach): {
  specific: string;
  video: string;
  call: string;
  reply24h: string;
  notes: string;
} {
  const m = coach.free30_process_metrics;
  const assigned = m?.free30_leads_assigned ?? 0;
  const pct = (n: number | undefined): string =>
    !m || assigned === 0 || n === undefined
      ? "—"
      : `${Math.round((n / assigned) * 100)}%`;
  return {
    specific: formatRate(m?.free_session_specificity_rate ?? null),
    video: pct(m?.free30_leads_with_manual_pre_session_url),
    call: "—",
    reply24h: pct(m?.free30_leads_with_manual_reply_pre_session),
    notes: pct(m?.free30_leads_with_notes_added)
  };
}

function RankingRow({ rank, rc, highlightCoachId }: IRankingRowProps) {
  const { coach } = rc;
  const isHighlighted = highlightCoachId === coach.person_id;
  const c = complianceCells(coach);
  return (
    <tr className={isHighlighted ? styles.highlighted : undefined}>
      <td className={styles.rank}>{rank ?? "—"}</td>
      <td>{coach.name}</td>
      <td>{gymDisplayName(rc.subdomain, rc.gymName)}</td>
      <td className={styles.score}>
        {rc.score === null ? "—" : rc.score.toFixed(1)}
      </td>
      <td className={styles.num}>
        {formatNumber(getFreeSessionsBooked(coach))}
      </td>
      <td className={styles.rate}>{formatRate(getShowRate(coach))}</td>
      <td className={styles.rate}>{formatRate(getConversionRate(coach))}</td>
      <td className={styles.rate}>{c.specific}</td>
      <td className={styles.rate}>{c.video}</td>
      <td className={styles.rate}>{c.call}</td>
      <td className={styles.rate}>{c.reply24h}</td>
      <td className={styles.rate}>{c.notes}</td>
    </tr>
  );
}

function RankingTable({
  rows,
  highlightCoachId
}: {
  rows: IRankedCoach[];
  highlightCoachId?: number | null;
}) {
  const ranked = rows.filter(r => !r.insufficientData);
  const insufficient = rows.filter(r => r.insufficientData);

  return (
    <>
      <div className={styles.tableWrap}>
        <table className={styles.rankingTable}>
          <thead>
            <tr>
              <th className={styles.rank}>#</th>
              <th>Coach</th>
              <th>Gym</th>
              <th className={styles.score}>Score</th>
              <th className={styles.num}>Booked</th>
              <th className={styles.rate}>Show</th>
              <th className={styles.rate}>Conv</th>
              <th>Specific</th>
              <th>Video</th>
              <th>Call</th>
              <th>24h</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {ranked.map((rc, i) => (
              <RankingRow
                key={`${rc.subdomain}:${rc.coach.person_id}`}
                rank={i + 1}
                rc={rc}
                highlightCoachId={highlightCoachId}
              />
            ))}
          </tbody>
        </table>
      </div>
      {insufficient.length > 0 && (
        <div className={styles.insufficientNote}>
          Insufficient data this period (fewer than 3 Free 30 bookings):
        </div>
      )}
      {insufficient.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.rankingTable}>
            <thead>
              <tr>
                <th className={styles.rank}>#</th>
                <th>Coach</th>
                <th>Gym</th>
                <th className={styles.score}>Score</th>
                <th className={styles.num}>Booked</th>
                <th className={styles.rate}>Show</th>
                <th className={styles.rate}>Conv</th>
                <th>Specific</th>
                <th>Video</th>
                <th>Call</th>
                <th>24h</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {insufficient.map(rc => (
                <RankingRow
                  key={`${rc.subdomain}:${rc.coach.person_id}`}
                  rank={null}
                  rc={rc}
                  highlightCoachId={highlightCoachId}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ------------------------------ Section ------------------------------- */

interface IProps {
  month: string;
  highlightCoachId?: number | null;
}

export default function GymWideSection({ month, highlightCoachId }: IProps) {
  const admin = useAdminFree30(month);

  // Keep section month in sync with the page's month picker.
  React.useEffect(() => {
    admin.setMonth(month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  if (!admin.configured) {
    return (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Gym-wide context</h2>
        </div>
        <p className={styles.sectionHint}>
          Coach Performance API not configured.
        </p>
      </section>
    );
  }

  return (
    <>
      {admin.errors.length > 0 && (
        <section className={styles.section}>
          <div className={styles.errorBanner}>
            Could not load:{" "}
            {admin.errors
              .map(
                e =>
                  `${gymDisplayName(e.subdomain)} (${e.error.error} — ${
                    e.error.message
                  })`
              )
              .join("; ")}
          </div>
        </section>
      )}

      {admin.loading && !admin.data && (
        <section className={styles.section}>
          <div className={styles.loadingBanner}>Loading gym-wide data…</div>
        </section>
      )}

      {admin.data && (
        <>
          {/* Headline tiles ----------------------------------------- */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Gym-wide headline</h2>
              <p className={styles.sectionHint}>
                {monthDisplayName(admin.data.month)} · across all loaded gyms
              </p>
            </div>
            <div className={styles.headlineGrid}>
              <div className={styles.headlineTile}>
                <span className={styles.headlineLabel}>Free 30 Bookings</span>
                <span className={styles.headlineValue}>
                  {admin.data.totals.bookings}
                </span>
                <span className={styles.headlineHint}>
                  across {admin.data.perGym.length} location
                  {admin.data.perGym.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className={styles.headlineTile}>
                <span className={styles.headlineLabel}>Show Rate</span>
                <span className={styles.headlineValue}>
                  {formatRate(admin.data.totals.showRate)}
                </span>
                <span className={styles.headlineHint}>
                  {admin.data.totals.shows} of {admin.data.totals.bookings}{" "}
                  attended
                </span>
              </div>
              <div className={styles.headlineTile}>
                <span className={styles.headlineLabel}>Conversion Rate</span>
                <span className={styles.headlineValue}>
                  {formatRate(admin.data.totals.conversionRate)}
                </span>
                <span className={styles.headlineHint}>
                  {admin.data.totals.conversions} of {admin.data.totals.shows}{" "}
                  converted
                </span>
              </div>
            </div>
          </section>

          {/* Overall ranking ---------------------------------------- */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Overall ranking</h2>
              <p className={styles.sectionHint}>
                Score = (conversion × 0.6) + (show × 0.4). 3-booking minimum to
                rank.
              </p>
            </div>
            <RankingTable
              rows={admin.data.ranked}
              highlightCoachId={highlightCoachId}
            />
          </section>

          {/* Per-gym breakdown -------------------------------------- */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Per-gym breakdown</h2>
            </div>
            {admin.data.perGym.map(gym => {
              const gymCoaches = admin.data!.ranked.filter(
                rc => rc.subdomain === gym.gym.subdomain
              );
              return (
                <div key={gym.gym.subdomain} className={styles.perGymBlock}>
                  <h3 className={styles.perGymHeading}>
                    {gymDisplayName(gym.gym.subdomain, gym.gym.name)}
                  </h3>
                  <RankingTable
                    rows={gymCoaches}
                    highlightCoachId={highlightCoachId}
                  />
                </div>
              );
            })}
          </section>

          {/* Per-KPI rankings -------------------------------------- */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Per-KPI rankings</h2>
              <p className={styles.sectionHint}>
                Top 5 · minimum 3 bookings (or attended sessions)
              </p>
            </div>
            <div className={styles.kpiRankGrid}>
              {KPI_RANKINGS.map(rank => (
                <div key={rank.key} className={styles.kpiRankCard}>
                  <h4 className={styles.kpiRankTitle}>{rank.title}</h4>
                  <ol className={styles.kpiRankList}>
                    {pickTopN(admin.data!.ranked, rank, 5).map((row, i) => (
                      <li key={`${rank.key}:${row.name}`}>
                        <span className={styles.kpiRankPos}>{i + 1}</span>
                        <span className={styles.kpiRankName}>
                          {row.name}{" "}
                          <span className={styles.headlineHint}>
                            · {row.gym}
                          </span>
                        </span>
                        <span className={styles.kpiRankValue}>
                          {row.detail} ({formatRate(row.rate)})
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
              <div className={styles.kpiRankCard}>
                <h4 className={styles.kpiRankTitle}>Compliance rankings</h4>
                <p className={styles.headlineHint}>
                  Per-coach Specificity, Video, Reply &lt;24h, and Notes
                  compliance live in the ranking tables above. Pre-Session
                  Call stays a dash here because the API exposes a total
                  across leads, not a per-lead boolean.
                </p>
              </div>
            </div>
          </section>

          {/* Methodology -------------------------------------------- */}
          <div className={styles.methodology}>
            <h3>Methodology</h3>
            <ul>
              <li>
                Composite score: (conversion × 0.6) + (show × 0.4). Adherence
                stats sit alongside for reference but do not affect the score.
              </li>
              <li>
                Stat-sig: coaches with fewer than 3 Free 30 bookings this month
                appear in the insufficient-data block with no rank.
              </li>
              <li>
                Compliance numbers (Specificity, Video, Reply &lt;24h, Notes)
                come from the Coach Performance API&apos;s
                `free30_process_metrics` block — same source as the per-coach
                Row 3 above. Pre-Session Call is rendered as a dash because
                the API exposes a total across leads, not a per-lead boolean.
              </li>
              <li>
                Data sourced from the Coach Performance API. Per-month, per gym.
                Refreshed daily after Pike13 sync.
              </li>
            </ul>
          </div>
        </>
      )}
    </>
  );
}
